import React from 'react';
import { motion } from 'framer-motion';
interface KofschipMnemonicProps {
  verb: string;
  stem: string;
  lastLetter: string;
  useTE: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function KofschipMnemonic({
  verb,
  stem,
  lastLetter,
  useTE,
  label,
  mode = 'support'
}: KofschipMnemonicProps) {
  const kofschipLetters = ['K', 'O', 'F', 'S', 'C', 'H', 'I', 'P'];
  const isInKofschip = kofschipLetters.includes(lastLetter.toUpperCase());
  // For the "not in kofschip" indicator shown below the grid
  const notInKofschipIndicator = !isInKofschip ? <div className="flex items-center justify-center gap-2 mt-2" data-id="element-12879">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg bg-rose-100 text-rose-600 border-2 border-rose-300 shadow-sm" data-id="element-12880">
        {lastLetter.toUpperCase()}
      </div>
      <span className="text-sm font-semibold text-rose-600" data-id="element-12881">
        ← niet in 't kofschip
      </span>
    </div> : null;
  // ── STIMULUS MODE: compact KOFSCHIP grid + result only ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-12882">
        {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-12883">
            {label}
          </p>}
        {/* Compact KOFSCHIP grid with color coding */}
        <div className="flex items-center gap-1.5" data-id="element-12884">
          <span className="text-base font-black text-slate-400 mr-1" data-id="element-12885">'t</span>
          {kofschipLetters.map(letter => {
          const isMatch = letter === lastLetter.toUpperCase();
          return <div key={letter} className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-lg shadow-sm
                  ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-id="element-12886">
                {letter}
              </div>;
        })}
        </div>
        {/* Show the tested letter in red if NOT in kofschip */}
        {notInKofschipIndicator}
        {/*
              NOTE (antwoord-lek fix): the previous "stem + -te/-de" result badge
              was REMOVED from stimulus mode. As a stimulus it pre-computed the
              full kofschip decision AND revealed the intact loanword stem-spelling
              — i.e. it handed the learner the exact answer the MC/fill-in item is
              meant to test (e.g. seed stem:"race", useTE:true → showed "race + -te"
              → option "racete"). The KOFSCHIP letter grid + "niet in 't kofschip"
              indicator stay: they present the *mnemonic tool* (and, for klank-vs-
              letter traps like 'race', the deliberate misconception) without
              resolving the answer. The full worked result still lives in SUPPORT
              mode below, shown via the rule/support aid, not in the stimulus.
            */}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-12887">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 md:p-8 border-2 border-blue-200 shadow-lg" data-id="element-12888">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-12889">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-12890">
            <span className="text-2xl" data-id="element-12891">⛵</span>
            <div className="text-left" data-id="element-12892">
              <div className="text-sm font-bold text-blue-900" data-id="element-12893">
                {label || "'t KOFSCHIP Regel"}
              </div>
              <div className="text-xs text-blue-600 font-semibold" data-id="element-12894">
                Verleden Tijd Spelling
              </div>
            </div>
          </div>
        </div>

        {/* Ezelsbruggetje (Tip) */}
        <div className="mb-6 bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-12895">
          <div className="flex items-start gap-3" data-id="element-12896">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-12897">
              <span className="text-amber-600 text-lg" data-id="element-12898">💡</span>
            </div>
            <div data-id="element-12899">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-12900">
                Ezelsbruggetje
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12901">
                Zit de laatste letter van de stam in{' '}
                <strong data-id="element-12902">'t KOFSCHIP</strong>?
                <br data-id="element-12903" />✅ Ja → <strong data-id="element-12904">-te(n)</strong>
                <br data-id="element-12905" />❌ Nee → <strong data-id="element-12906">-de(n)</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Stap-voor-stap voorbeeld (Compact) */}
        <div className="mb-6 bg-white/80 rounded-xl p-5 border border-emerald-100 shadow-sm" data-id="element-12907">
          <div className="flex items-start gap-3" data-id="element-12908">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12909">
              <span className="text-emerald-600 text-lg" data-id="element-12910">📝</span>
            </div>
            <div className="w-full" data-id="element-12911">
              <h4 className="text-sm font-bold text-emerald-900 mb-4" data-id="element-12912">
                Voorbeeld: {verb}
              </h4>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-between bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-12913">
                <div className="text-center" data-id="element-12914">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12915">Stam</div>
                  <div className="font-bold text-lg text-blue-700" data-id="element-12916">{stem}</div>
                </div>
                <div className="text-slate-400" data-id="element-12917">→</div>
                <div className="text-center" data-id="element-12918">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12919">
                    Laatste letter
                  </div>
                  <div className={`font-bold text-xl ${isInKofschip ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-12920">
                    {lastLetter.toUpperCase()}
                  </div>
                </div>
                <div className="text-slate-400" data-id="element-12921">→</div>
                <div className="text-center" data-id="element-12922">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12923">
                    In 't KOFSCHIP?
                  </div>
                  <div className="font-bold text-lg" data-id="element-12924">
                    {isInKofschip ? '✅ Ja' : '❌ Nee'}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center p-3 rounded-lg border-2 font-bold bg-white shadow-sm" data-id="element-12925">
                Dus: <span className="text-emerald-700" data-id="element-12926">{stem}</span> +{' '}
                <span className="text-emerald-700" data-id="element-12927">
                  {useTE ? 'te(n)' : 'de(n)'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Steps */}
        <details className="group bg-white/60 rounded-xl border border-blue-100 shadow-sm overflow-hidden" data-id="element-12928">
          <summary className="cursor-pointer p-4 font-semibold text-blue-800 hover:bg-blue-50/50 transition-colors flex items-center justify-between" data-id="element-12929">
            <span data-id="element-12930">Meer uitleg (stap voor stap)</span>
            <span className="text-blue-400 group-open:rotate-180 transition-transform" data-id="element-12931">
              ▼
            </span>
          </summary>
          <div className="p-5 border-t border-blue-100 space-y-6" data-id="element-12932">
            {/* Step 1: Verb → Stem */}
            <div className="flex items-start gap-3" data-id="element-12933">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12934">
                <span className="text-blue-600 font-bold text-xs" data-id="element-12935">1</span>
              </div>
              <div data-id="element-12936">
                <h4 className="text-sm font-bold text-blue-900 mb-1" data-id="element-12937">
                  Vind de stam
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12938">
                  Haal -en van het hele werkwoord af: <strong data-id="element-12939">{verb}</strong> →{' '}
                  <strong data-id="element-12940">{stem}</strong>
                </p>
              </div>
            </div>

            {/* Step 2: Last Letter Check */}
            <div className="flex items-start gap-3" data-id="element-12941">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12942">
                <span className="text-cyan-600 font-bold text-xs" data-id="element-12943">2</span>
              </div>
              <div data-id="element-12944">
                <h4 className="text-sm font-bold text-cyan-900 mb-1" data-id="element-12945">
                  Kijk naar de laatste letter
                </h4>
                <p className="text-sm text-slate-600" data-id="element-12946">
                  De laatste letter van de stam is de{' '}
                  <strong data-id="element-12947">{lastLetter.toUpperCase()}</strong>.
                </p>
              </div>
            </div>

            {/* Step 3: KOFSCHIP Check */}
            <div className="flex items-start gap-3" data-id="element-12948">
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-12949">
                <span className="text-indigo-600 font-bold text-xs" data-id="element-12950">3</span>
              </div>
              <div className="w-full" data-id="element-12951">
                <h4 className="text-sm font-bold text-indigo-900 mb-3" data-id="element-12952">
                  Zit deze in 't KOFSCHIP?
                </h4>

                <div className="flex justify-start gap-1.5 mb-3 flex-wrap" data-id="element-12953">
                  <div className="text-lg font-black text-slate-400 mr-1" data-id="element-12954">
                    't
                  </div>
                  {kofschipLetters.map(letter => {
                  const isMatch = letter === lastLetter.toUpperCase();
                  return <div key={letter} className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm shadow-sm
                          ${isMatch ? 'bg-emerald-500 text-white ring-2 ring-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-12955">
                        {letter}
                      </div>;
                })}
                </div>

                {!isInKofschip && <div className="text-sm text-rose-600 font-semibold mb-2" data-id="element-12956">
                    De {lastLetter.toUpperCase()} zit er NIET in.
                  </div>}
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>;
}